import { useState, useCallback, useEffect, useRef, type CSSProperties } from 'react';
import { useAuth } from '@helios/auth-firebase-web';
import { Button } from '@helios/ui/button';
import { Modal } from '@helios/ui/overlay';
import { NavIcon } from '../../shell/shared';
import { usePowerSyncQuery, usePowerSyncDb } from '../../adapters/powersync';
import {
  qAppointmentCategories,
  mapCategoryRowToUI,
  mapUIToCategoryColumns,
  qAppointmentStatuses,
  mapStatusRowToUI,
  mapUIToStatusColumns,
  type AppointmentCategoryRow,
  type AppointmentStatusRow,
  type AppointmentStatusType,
} from '@helios/data-access';
import { DEFAULT_APPOINTMENT_CATEGORIES, DEFAULT_APPOINTMENT_STATUSES } from '@helios/fixtures';

// ─── Types ───

interface Category {
  id: string;
  name: string;
  description: string;
  color: string;
  typicalDuration: number | null;
  active: boolean;
  sortOrder: number;
}

interface Status {
  id: string;
  name: string;
  type: AppointmentStatusType;
  color: string;
  active: boolean;
  sortOrder: number;
}

// ─── Color Options ───

const COLOR_OPTIONS = [
  '#3b82f6',
  '#22c55e',
  '#ef4444',
  '#f59e0b',
  '#8b5cf6',
  '#ec4899',
  '#14b8a6',
  '#f97316',
];

const STATUS_COLOR_OPTIONS = [
  '#22c55e',
  '#16a34a',
  '#f59e0b',
  '#f97316',
  '#ef4444',
  '#3b82f6',
  '#8b5cf6',
  '#ec4899',
];

// ─── Shared Styles ───

const thStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-semi)',
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  textAlign: 'left',
};

const actionBtnStyle: CSSProperties = {
  width: 32,
  height: 32,
  borderRadius: 'var(--radius-md)',
  background: 'transparent',
  border: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--text-muted)',
  cursor: 'pointer',
  padding: 0,
};

const labelStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-medium)',
  lineHeight: 'var(--line-height-sm)',
  color: 'var(--text)',
  display: 'block',
  marginBottom: 'var(--sp-1)',
};

const inputBaseStyle: CSSProperties = {
  height: 'var(--input-height)',
  padding: '0 var(--input-padding-x)',
  borderRadius: 'var(--input-radius)',
  border: '1px solid var(--border-input)',
  background: 'var(--input-bg)',
  fontSize: 'var(--font-size-base)',
  lineHeight: 'var(--line-height-base)',
  color: 'var(--input-text)',
  fontFamily: 'inherit',
  boxSizing: 'border-box' as const,
  width: '100%',
};

// ─── Toggle ───

function Toggle({ checked, onChange }: { checked: boolean; onChange: (val: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      style={{
        width: 44,
        height: 24,
        borderRadius: 'var(--radius-full)',
        background: checked ? 'var(--color-primary)' : 'var(--border)',
        border: 'none',
        padding: 2,
        cursor: 'pointer',
        transition: 'background 150ms ease',
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0,
      }}
    >
      <span
        style={{
          width: 20,
          height: 20,
          borderRadius: 'var(--radius-full)',
          background: '#ffffff',
          transform: checked ? 'translateX(20px)' : 'translateX(0)',
          transition: 'transform 150ms ease',
          boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
        }}
      />
    </button>
  );
}

// ─── Drag Handle ───

function DragHandle() {
  return (
    <div
      style={{
        width: 18,
        height: 18,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        color: 'var(--text-muted)',
        cursor: 'grab',
        flexShrink: 0,
      }}
    >
      <div style={{ display: 'flex', gap: 3 }}>
        <div style={{ width: 3, height: 3, borderRadius: 1, background: 'currentColor' }} />
        <div style={{ width: 3, height: 3, borderRadius: 1, background: 'currentColor' }} />
      </div>
      <div style={{ display: 'flex', gap: 3 }}>
        <div style={{ width: 3, height: 3, borderRadius: 1, background: 'currentColor' }} />
        <div style={{ width: 3, height: 3, borderRadius: 1, background: 'currentColor' }} />
      </div>
      <div style={{ display: 'flex', gap: 3 }}>
        <div style={{ width: 3, height: 3, borderRadius: 1, background: 'currentColor' }} />
        <div style={{ width: 3, height: 3, borderRadius: 1, background: 'currentColor' }} />
      </div>
    </div>
  );
}

// ─── Color Dot ───

function ColorDot({ color, size = 14 }: { color: string; size?: number }) {
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: 'var(--radius-full)',
        background: color,
        flexShrink: 0,
      }}
    />
  );
}

// ─── Status Badge ───

function StatusBadge({ name, color }: { name: string; color: string }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 26,
        padding: '0 var(--sp-3)',
        borderRadius: 'var(--radius-full)',
        background: color,
        color: '#fff',
        fontSize: 'var(--font-size-xs)',
        fontWeight: 'var(--font-weight-semi)',
        whiteSpace: 'nowrap',
      }}
    >
      {name}
    </span>
  );
}

// ─── Color Picker (simple) ───

function ColorPicker({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (color: string) => void;
  options: string[];
}) {
  return (
    <div style={{ display: 'flex', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
      {options.map((c) => (
        <button
          key={c}
          type="button"
          onClick={() => onChange(c)}
          style={{
            width: 28,
            height: 28,
            borderRadius: 'var(--radius-full)',
            background: c,
            border: value === c ? '2px solid var(--text)' : '2px solid transparent',
            outline: value === c ? '2px solid var(--bg-surface)' : 'none',
            cursor: 'pointer',
            padding: 0,
          }}
        />
      ))}
    </div>
  );
}

// ─── Section Card ───

function SectionCard({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        background: 'var(--bg-surface)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--border)',
        padding: 'var(--sp-6)',
        marginBottom: 'var(--sp-6)',
      }}
    >
      {children}
    </div>
  );
}

// ─── Category Modal ───

interface CategoryFormState {
  name: string;
  description: string;
  color: string;
  typicalDuration: string;
  active: boolean;
}

function defaultCategoryForm(cat?: Category | null): CategoryFormState {
  if (cat) {
    return {
      name: cat.name,
      description: cat.description,
      color: cat.color,
      typicalDuration: cat.typicalDuration != null ? String(cat.typicalDuration) : '',
      active: cat.active,
    };
  }
  return { name: '', description: '', color: '#3b82f6', typicalDuration: '', active: true };
}

function CategoryModal({
  open,
  onOpenChange,
  category,
  onSave,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  category: Category | null;
  onSave: (cat: Omit<Category, 'id' | 'sortOrder'> & { id?: string; sortOrder?: number }) => void;
}) {
  const [form, setForm] = useState<CategoryFormState>(defaultCategoryForm(category));

  const handleSave = () => {
    const dur = form.typicalDuration.trim();
    onSave({
      id: category?.id,
      name: form.name,
      description: form.description,
      color: form.color,
      typicalDuration: dur === '' ? null : parseInt(dur, 10),
      active: form.active,
      sortOrder: category?.sortOrder,
    });
    onOpenChange(false);
  };

  const isEdit = !!category;

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={isEdit ? 'Edit Category' : 'New Category'}
      maxWidth={440}
      footer={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Button variant="outline" size="lg" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="gradient" size="lg" onClick={handleSave} disabled={!form.name.trim()}>
            {isEdit ? 'Save Changes' : 'Create Category'}
          </Button>
        </div>
      }
    >
      <div
        style={{
          padding: 'var(--sp-6, 24px)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-5, 20px)',
        }}
      >
        {/* Name */}
        <div>
          <label style={labelStyle}>
            Name<span style={{ color: 'var(--color-danger)' }}> *</span>
          </label>
          <input
            type="text"
            placeholder="e.g. Consultation"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            style={inputBaseStyle}
          />
        </div>

        {/* Description */}
        <div>
          <label style={labelStyle}>Description</label>
          <input
            type="text"
            placeholder="Brief description"
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            style={inputBaseStyle}
          />
        </div>

        {/* Color */}
        <div>
          <label style={labelStyle}>Color</label>
          <ColorPicker
            value={form.color}
            onChange={(c) => setForm((f) => ({ ...f, color: c }))}
            options={COLOR_OPTIONS}
          />
        </div>

        {/* Typical Duration */}
        <div>
          <label style={labelStyle}>Typical Duration (mins)</label>
          <input
            type="number"
            placeholder="e.g. 30"
            min={0}
            value={form.typicalDuration}
            onChange={(e) => setForm((f) => ({ ...f, typicalDuration: e.target.value }))}
            style={inputBaseStyle}
          />
        </div>

        {/* Active */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <label style={{ ...labelStyle, marginBottom: 0 }}>Active</label>
          <Toggle checked={form.active} onChange={(v) => setForm((f) => ({ ...f, active: v }))} />
        </div>
      </div>
    </Modal>
  );
}

// ─── Status Modal ───

interface StatusFormState {
  name: string;
  type: AppointmentStatusType;
  color: string;
  active: boolean;
}

function defaultStatusForm(s?: Status | null): StatusFormState {
  if (s) return { name: s.name, type: s.type, color: s.color, active: s.active };
  return { name: '', type: 'normal', color: '#22c55e', active: true };
}

function StatusModal({
  open,
  onOpenChange,
  status,
  onSave,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  status: Status | null;
  onSave: (s: Omit<Status, 'id' | 'sortOrder'> & { id?: string; sortOrder?: number }) => void;
}) {
  const [form, setForm] = useState<StatusFormState>(defaultStatusForm(status));

  const handleSave = () => {
    onSave({
      id: status?.id,
      name: form.name,
      type: form.type,
      color: form.color,
      active: form.active,
      sortOrder: status?.sortOrder,
    });
    onOpenChange(false);
  };

  const isEdit = !!status;

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={isEdit ? 'Edit Status' : 'New Status'}
      maxWidth={440}
      footer={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Button variant="outline" size="lg" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="gradient" size="lg" onClick={handleSave} disabled={!form.name.trim()}>
            {isEdit ? 'Save Changes' : 'Create Status'}
          </Button>
        </div>
      }
    >
      <div
        style={{
          padding: 'var(--sp-6, 24px)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-5, 20px)',
        }}
      >
        {/* Name */}
        <div>
          <label style={labelStyle}>
            Name<span style={{ color: 'var(--color-danger)' }}> *</span>
          </label>
          <input
            type="text"
            placeholder="e.g. Scheduled"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            style={inputBaseStyle}
          />
        </div>

        {/* Type */}
        <div>
          <label style={labelStyle}>Type</label>
          <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
            {(['normal', 'terminal'] as const).map((t) => {
              const selected = form.type === t;
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, type: t }))}
                  style={{
                    height: 'var(--input-height)',
                    padding: '0 var(--input-padding-x)',
                    borderRadius: 'var(--input-radius)',
                    background: selected ? 'var(--color-primary)' : 'var(--input-bg)',
                    color: selected ? 'var(--text-on-primary, #fff)' : 'var(--input-text)',
                    border: selected
                      ? '1px solid var(--color-primary)'
                      : '1px solid var(--border-input)',
                    fontSize: 'var(--font-size-base)',
                    fontWeight: 'var(--font-weight-medium)',
                    fontFamily: 'inherit',
                    cursor: 'pointer',
                    textTransform: 'capitalize',
                  }}
                >
                  {t}
                </button>
              );
            })}
          </div>
        </div>

        {/* Color */}
        <div>
          <label style={labelStyle}>Badge Color</label>
          <ColorPicker
            value={form.color}
            onChange={(c) => setForm((f) => ({ ...f, color: c }))}
            options={STATUS_COLOR_OPTIONS}
          />
        </div>

        {/* Active */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <label style={{ ...labelStyle, marginBottom: 0 }}>Active</label>
          <Toggle checked={form.active} onChange={(v) => setForm((f) => ({ ...f, active: v }))} />
        </div>
      </div>
    </Modal>
  );
}

// ─── UUID helper ───

function uuid(): string {
  return crypto.randomUUID();
}

// ─── Main Page ───

export function CategoryStatusesPage() {
  const { claims } = useAuth();
  const orgId = claims?.activeOrg ?? '';
  const { db } = usePowerSyncDb();
  const { data: catRows = [] } =
    usePowerSyncQuery<AppointmentCategoryRow>(qAppointmentCategories());
  const { data: statusRows = [] } = usePowerSyncQuery<AppointmentStatusRow>(qAppointmentStatuses());

  // Map DB rows to UI types
  const dbCategories: Category[] = catRows.map(mapCategoryRowToUI);
  const dbStatuses: Status[] = statusRows.map(mapStatusRowToUI);

  // ── Buffered local state ──
  const [localCategories, setLocalCategories] = useState<Category[]>([]);
  const [localStatuses, setLocalStatuses] = useState<Status[]>([]);
  const [savedSnapshot, setSavedSnapshot] = useState('');
  const initializedRef = useRef(false);
  const [saving, setSaving] = useState(false);

  // Sync DB data into local state on first load (or when DB changes externally and no local edits)
  useEffect(() => {
    const dbSnap = JSON.stringify({ c: dbCategories, s: dbStatuses });
    if (!initializedRef.current && (dbCategories.length > 0 || dbStatuses.length > 0)) {
      setLocalCategories(dbCategories);
      setLocalStatuses(dbStatuses);
      setSavedSnapshot(dbSnap);
      initializedRef.current = true;
    } else if (initializedRef.current && dbSnap === savedSnapshot) {
      // DB matches our saved snapshot — no external changes
    } else if (initializedRef.current) {
      // DB changed externally (e.g. sync) and we have no local edits — re-sync
      const currentSnap = JSON.stringify({ c: localCategories, s: localStatuses });
      if (currentSnap === savedSnapshot) {
        setLocalCategories(dbCategories);
        setLocalStatuses(dbStatuses);
        setSavedSnapshot(dbSnap);
      }
    }
  }, [dbCategories, dbStatuses]);

  const isDirty = JSON.stringify({ c: localCategories, s: localStatuses }) !== savedSnapshot;

  // Category modal state
  const [catModalOpen, setCatModalOpen] = useState(false);
  const [editingCat, setEditingCat] = useState<Category | null>(null);
  const [deleteCatTarget, setDeleteCatTarget] = useState<Category | null>(null);

  // Status modal state
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [editingStatus, setEditingStatus] = useState<Status | null>(null);
  const [deleteStatusTarget, setDeleteStatusTarget] = useState<Status | null>(null);

  // Queue rules state
  const [allowManualQueue, setAllowManualQueue] = useState(true);

  // Toast
  const [toast, setToast] = useState('');
  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2500);
  }, []);

  // ── Category handlers (modify local state only) ──

  const handleNewCategory = () => {
    setEditingCat(null);
    setCatModalOpen(true);
  };

  const handleEditCategory = (cat: Category) => {
    setEditingCat(cat);
    setCatModalOpen(true);
  };

  const handleSaveCategory = useCallback(
    (cat: Omit<Category, 'id' | 'sortOrder'> & { id?: string; sortOrder?: number }) => {
      if (cat.id) {
        // Update existing
        setLocalCategories((prev) =>
          prev.map((c) =>
            c.id === cat.id
              ? {
                  ...c,
                  name: cat.name,
                  description: cat.description,
                  color: cat.color,
                  typicalDuration: cat.typicalDuration,
                  active: cat.active,
                }
              : c,
          ),
        );
      } else {
        // Create new
        const newCat: Category = {
          id: uuid(),
          name: cat.name,
          description: cat.description,
          color: cat.color,
          typicalDuration: cat.typicalDuration,
          active: cat.active,
          sortOrder: cat.sortOrder ?? (localCategories.length + 1) * 10,
        };
        setLocalCategories((prev) => [...prev, newCat]);
      }
    },
    [localCategories.length],
  );

  const handleDeleteCategory = useCallback(() => {
    if (!deleteCatTarget) return;
    setLocalCategories((prev) => prev.filter((c) => c.id !== deleteCatTarget.id));
    setDeleteCatTarget(null);
  }, [deleteCatTarget]);

  const handleToggleCategoryActive = useCallback((cat: Category) => {
    setLocalCategories((prev) =>
      prev.map((c) => (c.id === cat.id ? { ...c, active: !c.active } : c)),
    );
  }, []);

  // ── Status handlers (modify local state only) ──

  const handleNewStatus = () => {
    setEditingStatus(null);
    setStatusModalOpen(true);
  };

  const handleEditStatus = (s: Status) => {
    setEditingStatus(s);
    setStatusModalOpen(true);
  };

  const handleSaveStatus = useCallback(
    (s: Omit<Status, 'id' | 'sortOrder'> & { id?: string; sortOrder?: number }) => {
      if (s.id) {
        setLocalStatuses((prev) =>
          prev.map((st) =>
            st.id === s.id
              ? { ...st, name: s.name, type: s.type, color: s.color, active: s.active }
              : st,
          ),
        );
      } else {
        const newStatus: Status = {
          id: uuid(),
          name: s.name,
          type: s.type,
          color: s.color,
          active: s.active,
          sortOrder: s.sortOrder ?? (localStatuses.length + 1) * 10,
        };
        setLocalStatuses((prev) => [...prev, newStatus]);
      }
    },
    [localStatuses.length],
  );

  const handleDeleteStatus = useCallback(() => {
    if (!deleteStatusTarget) return;
    setLocalStatuses((prev) => prev.filter((s) => s.id !== deleteStatusTarget.id));
    setDeleteStatusTarget(null);
  }, [deleteStatusTarget]);

  const handleToggleStatusActive = useCallback((s: Status) => {
    setLocalStatuses((prev) =>
      prev.map((st) => (st.id === s.id ? { ...st, active: !s.active } : st)),
    );
  }, []);

  // ── Reset to defaults (local state only, persisted on Save) ──

  const handleResetToDefaults = useCallback(() => {
    setLocalCategories(
      DEFAULT_APPOINTMENT_CATEGORIES.map((cat) => ({
        id: uuid(),
        name: cat.name,
        description: cat.description,
        color: cat.color_hex,
        typicalDuration: cat.typical_duration_mins,
        active: true,
        sortOrder: cat.sort_order,
      })),
    );
    setLocalStatuses(
      DEFAULT_APPOINTMENT_STATUSES.map((s) => ({
        id: uuid(),
        name: s.label,
        type: s.type as AppointmentStatusType,
        color: s.color_hex,
        active: true,
        sortOrder: s.sort_order,
      })),
    );
  }, []);

  // ── Save Changes — persist buffered state to DB ──

  const handleSave = useCallback(async () => {
    if (!db) return;
    setSaving(true);
    try {
      const snapshot: { c: Category[]; s: Status[] } = savedSnapshot
        ? JSON.parse(savedSnapshot)
        : { c: [], s: [] };
      const savedCatIds = new Set(snapshot.c.map((c) => c.id));
      const savedStatusIds = new Set(snapshot.s.map((s) => s.id));
      const localCatIds = new Set(localCategories.map((c) => c.id));
      const localStatusIds = new Set(localStatuses.map((s) => s.id));
      const now = new Date().toISOString();

      // ── Categories: DELETE removed ──
      for (const id of savedCatIds) {
        if (!localCatIds.has(id)) {
          await db.execute('DELETE FROM appointment_categories WHERE id = ?', [id]);
        }
      }

      // ── Categories: INSERT new, UPDATE existing ──
      for (const cat of localCategories) {
        const cols = mapUIToCategoryColumns({
          name: cat.name,
          description: cat.description,
          color: cat.color,
          typicalDuration: cat.typicalDuration,
          active: cat.active,
          sortOrder: cat.sortOrder,
        });
        if (savedCatIds.has(cat.id)) {
          await db.execute(
            `UPDATE appointment_categories SET
              name = ?, description = ?, color_hex = ?,
              typical_duration_mins = ?, active = ?, sort_order = ?, updated_at = ?
            WHERE id = ?`,
            [
              cols.name,
              cols.description,
              cols.color_hex,
              cols.typical_duration_mins,
              cols.active,
              cols.sort_order,
              now,
              cat.id,
            ],
          );
        } else {
          await db.execute(
            `INSERT INTO appointment_categories
              (id, org_id, clinic_id, name, description, color_hex, typical_duration_mins, active, sort_order, created_at, updated_at)
            VALUES (?, ?, NULL, ?, ?, ?, ?, ?, ?, ?, ?)`,
            [
              cat.id,
              orgId,
              cols.name,
              cols.description,
              cols.color_hex,
              cols.typical_duration_mins,
              cols.active,
              cols.sort_order,
              now,
              now,
            ],
          );
        }
      }

      // ── Statuses: DELETE removed ──
      for (const id of savedStatusIds) {
        if (!localStatusIds.has(id)) {
          await db.execute('DELETE FROM appointment_statuses WHERE id = ?', [id]);
        }
      }

      // ── Statuses: INSERT new, UPDATE existing ──
      for (const s of localStatuses) {
        const cols = mapUIToStatusColumns({
          name: s.name,
          type: s.type,
          color: s.color,
          active: s.active,
          sortOrder: s.sortOrder,
        });
        if (savedStatusIds.has(s.id)) {
          await db.execute(
            `UPDATE appointment_statuses SET
              label = ?, type = ?, color_hex = ?, active = ?, sort_order = ?, updated_at = ?
            WHERE id = ?`,
            [cols.label, cols.type, cols.color_hex, cols.active, cols.sort_order, now, s.id],
          );
        } else {
          await db.execute(
            `INSERT INTO appointment_statuses
              (id, org_id, clinic_id, label, type, color_hex, active, sort_order, created_at, updated_at)
            VALUES (?, ?, NULL, ?, ?, ?, ?, ?, ?, ?)`,
            [
              s.id,
              orgId,
              cols.label,
              cols.type,
              cols.color_hex,
              cols.active,
              cols.sort_order,
              now,
              now,
            ],
          );
        }
      }

      const newSnap = JSON.stringify({ c: localCategories, s: localStatuses });
      setSavedSnapshot(newSnap);
      showToast('Changes saved');
    } catch (err) {
      console.error('[CategoryStatuses] save error:', err);
      showToast('Failed to save changes');
    } finally {
      setSaving(false);
    }
  }, [db, orgId, localCategories, localStatuses, savedSnapshot, showToast]);

  // ── Grid templates ──

  const catGridCols = '28px 160px 1fr 120px 70px 80px';
  const statusGridCols = '28px 160px 1fr 70px 80px';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Page Header */}
      <div
        style={{
          height: 'var(--clinic-header-height)',
          padding: '0 var(--sp-6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid var(--border)',
          flexShrink: 0,
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
            <h1
              style={{
                fontSize: 'var(--font-size-xl2)',
                fontWeight: 'var(--font-weight-semi)',
                lineHeight: 'var(--line-height-xl2)',
                letterSpacing: '-0.44px',
                color: 'var(--text)',
                margin: 0,
              }}
            >
              Category & Statuses
            </h1>
            <span
              style={{
                height: 22,
                padding: '0 var(--sp-2)',
                borderRadius: 'var(--radius-xs)',
                background: 'var(--color-warning-bg)',
                color: 'var(--color-warning-text)',
                fontSize: 'var(--font-size-xs)',
                fontWeight: 'var(--font-weight-medium)',
                lineHeight: '22px',
                letterSpacing: '0.12px',
              }}
            >
              Admin only
            </span>
          </div>
          <p
            style={{
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text-muted)',
              margin: '2px 0 0',
            }}
          >
            Manage appointment categories and status options used in Calendar and Live View
          </p>
        </div>

        <div style={{ display: 'flex', gap: 'var(--sp-3)', alignItems: 'center' }}>
          <Button variant="outline" size="md" onClick={handleResetToDefaults}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
              <NavIcon name="refresh" />
              Reset to Default
            </span>
          </Button>
          <Button variant="gradient" size="md" onClick={handleSave} disabled={!isDirty || saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {/* Scrollable Content */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: 'var(--sp-6)',
        }}
      >
        {/* ══════ Categories Section ══════ */}
        <SectionCard>
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              marginBottom: 'var(--sp-5)',
            }}
          >
            <div>
              <h2
                style={{
                  fontSize: 'var(--font-size-lg)',
                  fontWeight: 'var(--font-weight-semi)',
                  color: 'var(--text)',
                  margin: 0,
                }}
              >
                Categories
              </h2>
              <p
                style={{
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--text-muted)',
                  margin: '2px 0 0',
                }}
              >
                Categories control color-coding and filtering of appointments
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={handleNewCategory}>
              + New Category
            </Button>
          </div>

          {/* Categories Table Header */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: catGridCols,
              gap: 'var(--sp-3)',
              alignItems: 'center',
              padding: 'var(--sp-2) var(--sp-3)',
              borderBottom: '1px solid var(--border)',
            }}
          >
            <span style={thStyle}>COLOR</span>
            <span style={thStyle}>NAME</span>
            <span style={thStyle}>DESCRIPTION</span>
            <span style={thStyle}>DURATION</span>
            <span style={thStyle}>ACTIVE</span>
            <span style={thStyle}>ACTIONS</span>
          </div>

          {/* Category Rows */}
          {localCategories.map((cat) => (
            <div
              key={cat.id}
              style={{
                display: 'grid',
                gridTemplateColumns: catGridCols,
                gap: 'var(--sp-3)',
                alignItems: 'center',
                padding: 'var(--sp-3)',
                borderBottom: '1px solid var(--border)',
              }}
            >
              <ColorDot color={cat.color} />
              <span
                style={{
                  fontSize: 'var(--font-size-base)',
                  fontWeight: 'var(--font-weight-medium)',
                  color: 'var(--text)',
                }}
              >
                {cat.name}
              </span>
              <span
                style={{
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--text-muted)',
                }}
              >
                {cat.description}
              </span>
              <span
                style={{
                  fontSize: 'var(--font-size-sm)',
                  color: cat.typicalDuration != null ? 'var(--text)' : 'var(--text-muted)',
                }}
              >
                {cat.typicalDuration != null ? `${cat.typicalDuration} mins` : '—'}
              </span>
              <Toggle checked={cat.active} onChange={() => handleToggleCategoryActive(cat)} />
              <div style={{ display: 'flex', gap: 'var(--sp-1)' }}>
                <button
                  onClick={() => handleEditCategory(cat)}
                  aria-label="Edit category"
                  style={actionBtnStyle}
                >
                  <NavIcon name="edit" />
                </button>
                <button
                  onClick={() => setDeleteCatTarget(cat)}
                  aria-label="Delete category"
                  style={{ ...actionBtnStyle, color: 'var(--color-danger)' }}
                >
                  <NavIcon name="trash" />
                </button>
              </div>
            </div>
          ))}
        </SectionCard>

        {/* ══════ Statuses Section ══════ */}
        <SectionCard>
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              marginBottom: 'var(--sp-5)',
            }}
          >
            <div>
              <h2
                style={{
                  fontSize: 'var(--font-size-lg)',
                  fontWeight: 'var(--font-weight-semi)',
                  color: 'var(--text)',
                  margin: 0,
                }}
              >
                Statuses
              </h2>
              <p
                style={{
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--text-muted)',
                  margin: '2px 0 0',
                }}
              >
                Statuses control appointment lifecycle and filtering
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={handleNewStatus}>
              + New Status
            </Button>
          </div>

          {/* Statuses Table Header */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: statusGridCols,
              gap: 'var(--sp-3)',
              alignItems: 'center',
              padding: 'var(--sp-2) var(--sp-3)',
              borderBottom: '1px solid var(--border)',
            }}
          >
            <span /> {/* drag handle col */}
            <span style={thStyle}>STATUS</span>
            <span style={thStyle}>TYPE</span>
            <span style={thStyle}>ACTIVE</span>
            <span style={thStyle}>ACTIONS</span>
          </div>

          {/* Status Rows */}
          {localStatuses.map((s) => (
            <div
              key={s.id}
              style={{
                display: 'grid',
                gridTemplateColumns: statusGridCols,
                gap: 'var(--sp-3)',
                alignItems: 'center',
                padding: 'var(--sp-3)',
                borderBottom: '1px solid var(--border)',
              }}
            >
              <DragHandle />
              <StatusBadge name={s.name} color={s.color} />
              <span
                style={{
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--text-muted)',
                  textTransform: 'capitalize',
                }}
              >
                {s.type === 'terminal' ? 'Terminal' : ''}
              </span>
              <Toggle checked={s.active} onChange={() => handleToggleStatusActive(s)} />
              <div style={{ display: 'flex', gap: 'var(--sp-1)' }}>
                <button
                  onClick={() => handleEditStatus(s)}
                  aria-label="Edit status"
                  style={actionBtnStyle}
                >
                  <NavIcon name="edit" />
                </button>
                <button
                  onClick={() => setDeleteStatusTarget(s)}
                  aria-label="Delete status"
                  style={{ ...actionBtnStyle, color: 'var(--color-danger)' }}
                >
                  <NavIcon name="trash" />
                </button>
              </div>
            </div>
          ))}
        </SectionCard>

        {/* ══════ Queue Rules Section ══════ */}
        <SectionCard>
          <h2
            style={{
              fontSize: 'var(--font-size-lg)',
              fontWeight: 'var(--font-weight-semi)',
              color: 'var(--text)',
              margin: 0,
            }}
          >
            Queue Rules
          </h2>
          <p
            style={{
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text-muted)',
              margin: '2px 0 var(--sp-5)',
            }}
          >
            Define when an appointment should appear in Queue (check-in workflow)
          </p>

          {/* Create queue entry when */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: 'var(--sp-4) 0',
              borderBottom: '1px solid var(--border)',
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 'var(--font-size-base)',
                  fontWeight: 'var(--font-weight-semi)',
                  color: 'var(--text)',
                }}
              >
                Create queue entry when
              </div>
              <div
                style={{
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--text-muted)',
                  marginTop: 2,
                }}
              >
                Automatically add appointment to queue when status changes
              </div>
            </div>
            <select
              style={{
                ...inputBaseStyle,
                width: 180,
                cursor: 'pointer',
              }}
              defaultValue=""
            >
              <option value="" disabled>
                Select status...
              </option>
              {localStatuses.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>

          {/* Allow manual add-to-queue */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: 'var(--sp-4) 0',
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 'var(--font-size-base)',
                  fontWeight: 'var(--font-weight-semi)',
                  color: 'var(--text)',
                }}
              >
                Allow manual add-to-queue
              </div>
              <div
                style={{
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--text-muted)',
                  marginTop: 2,
                }}
              >
                Allow staff to manually add appointments to queue regardless of status
              </div>
            </div>
            <Toggle checked={allowManualQueue} onChange={setAllowManualQueue} />
          </div>

          {/* Note */}
          <div
            style={{
              marginTop: 'var(--sp-4)',
              padding: 'var(--sp-3) var(--sp-4)',
              borderRadius: 'var(--radius-md)',
              background: 'var(--color-info-bg, #eff6ff)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text)',
              lineHeight: 'var(--line-height-sm)',
            }}
          >
            <strong>Note:</strong> Queue workflow controls when appointments automatically appear in
            the check-in queue, helping staff manage patient flow.
          </div>
        </SectionCard>
      </div>

      {/* Toast */}
      {toast && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'var(--text)',
            color: '#fff',
            padding: 'var(--sp-3) var(--sp-5)',
            borderRadius: 'var(--radius-lg)',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-medium)',
            zIndex: 10000,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}
        >
          {toast}
        </div>
      )}

      {/* ── Modals ── */}

      {/* Category Create/Edit Modal */}
      <CategoryModal
        key={editingCat?.id ?? 'new-cat'}
        open={catModalOpen}
        onOpenChange={setCatModalOpen}
        category={editingCat}
        onSave={handleSaveCategory}
      />

      {/* Category Delete Confirm */}
      <Modal
        open={!!deleteCatTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteCatTarget(null);
        }}
        title="Delete Category"
        maxWidth={420}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-3)' }}>
            <Button variant="outline" size="md" onClick={() => setDeleteCatTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="solid"
              size="md"
              onClick={handleDeleteCategory}
              style={{ background: 'var(--color-danger)', color: '#fff' }}
            >
              Delete
            </Button>
          </div>
        }
      >
        <p style={{ color: 'var(--text)', fontSize: 'var(--font-size-base)', margin: 0 }}>
          Are you sure you want to delete <strong>{deleteCatTarget?.name}</strong>? This action
          cannot be undone.
        </p>
      </Modal>

      {/* Status Create/Edit Modal */}
      <StatusModal
        key={editingStatus?.id ?? 'new-status'}
        open={statusModalOpen}
        onOpenChange={setStatusModalOpen}
        status={editingStatus}
        onSave={handleSaveStatus}
      />

      {/* Status Delete Confirm */}
      <Modal
        open={!!deleteStatusTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteStatusTarget(null);
        }}
        title="Delete Status"
        maxWidth={420}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-3)' }}>
            <Button variant="outline" size="md" onClick={() => setDeleteStatusTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="solid"
              size="md"
              onClick={handleDeleteStatus}
              style={{ background: 'var(--color-danger)', color: '#fff' }}
            >
              Delete
            </Button>
          </div>
        }
      >
        <p style={{ color: 'var(--text)', fontSize: 'var(--font-size-base)', margin: 0 }}>
          Are you sure you want to delete <strong>{deleteStatusTarget?.name}</strong>? This action
          cannot be undone.
        </p>
      </Modal>
    </div>
  );
}
